/**
 * The once-per-flow-mount gate behind `pf_revamp.flow_loaded` / legacy
 * `order_flow_loaded`.
 *
 * This event is the funnel's denominator, so it has to fire exactly once per
 * visit to the flow, whichever screen the visit happens to start on. The screens
 * themselves cannot own that rule — each one only knows it has been entered, not
 * whether it was entered first — so the gate lives here and every screen simply
 * announces itself.
 *
 * Module-level state is deliberate: a React ref would live in one component, and
 * the screens that need to be gated are siblings under the router, not children
 * of a common mount. The flow shell resets it when it unmounts, which is what
 * makes a second visit in the same tab count as a second load.
 */

import { markOrderFlowLoadReported } from 'utils/analytics/orderFlowLoad'
import type { FlowLoadedPayload } from '../analytics/events'
import { PfRevampEventName } from '../analytics/events'
import { dispatchPfRevampEvent } from '../analytics/usePfRevampAnalytics'

let announced = false

/** Called by the flow shell on unmount, so the next entry counts again. */
export const resetFlowLoadedGate = (): void => {
  announced = false
}

/**
 * Announce that a real screen has rendered. The first call per flow mount fires
 * `flow_loaded`; every later one is ignored.
 */
export const announceFlowLoaded = (
  firstScreen: FlowLoadedPayload['firstScreen'],
  failureReason?: FlowLoadedPayload['failureReason']
): void => {
  if (announced) return
  announced = true
  markOrderFlowLoadReported('order_flow')
  dispatchPfRevampEvent({
    type: PfRevampEventName.FLOW_LOADED,
    payload: failureReason ? { firstScreen, failureReason } : { firstScreen },
  })
}
